import React, {useState, useEffect} from 'react';
import {t} from 'i18next';
import {Box} from '@mui/material';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import './CustomizeTagWidget.scss';
import ArrBanner from '../../../common/arr-banner/ArrBanner';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {WidgetsHeader} from '../widgets-header/WidgetsHeader';
import {
  useRedactionTag,
  useSaveTagClassificationDetails,
  useTagTreeData,
} from '../../../../hooks/dashboard/useDashboard';
import {
  formatTagClassificationTreeData,
  formatTagTreeData,
  wrapWithRootNode,
} from '../../../../utils/formatTagTreeData';
import {ReviewTagCount, Datum, TaggingWidgetData} from '../../dashboardTypes';
import {useQueryClient} from '@tanstack/react-query';
import {useTagClassifications} from '../../../../hooks/dashboard/useDashboard';
import {widgetIdMapping} from '../../dashboardUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';

type CustomizeTagWidgetProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
  reviewTagCountData?: {data?: Datum[]};
  onSuccess: (newData: TaggingWidgetData) => void;
};

export const CustomizeTagWidget: React.FC<CustomizeTagWidgetProps> = ({
  showDrawer,
  handleDrawer,
  reviewTagCountData,
  onSuccess,
}) => {
  const {
    data: tagData,
    refetch: refetchTags,
    isLoading: isTagLoading,
  } = useTagTreeData();
  const {
    data: classificationData,
    refetch: refetchClassification,
    isLoading: isClassificationLoading,
  } = useTagClassifications();
  const {
    data: redactionData,
    refetch: refetchRedaction,
    isLoading: isRedationLoading,
  } = useRedactionTag();
  const [selectedItem, setSelectedItem] = useState<string[] | null>([]);
  const [selectedClassificationItems, setSelectedClassificationItems] =
    useState<string[] | null>([]);
  const [selectedRedactionItems, setSelectedRedactionItems] = useState<
    string[] | null
  >([]);
  const [treeData, setTreeData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [classificationTreeData, setClassificationTreeData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [redactionTreeData, setRedactionTreeData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const MAX_COUNT = 6;
  const REDACTION_ROOT_ID = 'root:redaction-root';
  const REDACTION_ROOT_LABEL = t('tagRedaction.rootLabel');
  const CLASSIFICATION_ROOT_ID = 'root:tag-classifications';
  const CLASSIFICATION_ROOT_LABEL = t('tagClassification.rootLabel');
  const TAGS_ROOT_ID = 'root:tags-root';
  const TAGS_ROOT_LABEL = t('tagAll.rootLabel');
  useEffect(() => {
    if (showDrawer) {
      refetchTags();
      refetchClassification();
      refetchRedaction();
    }
  }, [showDrawer, refetchTags, refetchClassification, refetchRedaction]);

  useEffect(() => {
    if (tagData && Array.isArray(tagData)) {
      const formatted = formatTagTreeData(tagData);
      setTreeData(formatted);
    }
  }, [tagData]);
  useEffect(() => {
    if (Array.isArray(classificationData)) {
      setClassificationTreeData(
        formatTagClassificationTreeData(classificationData, {
          wrapWithRoot: true,
          rootId: CLASSIFICATION_ROOT_ID,
          rootLabel: CLASSIFICATION_ROOT_LABEL,
        })
      );
    } else {
      setClassificationTreeData([]);
    }
  }, [classificationData, CLASSIFICATION_ROOT_ID, CLASSIFICATION_ROOT_LABEL]);
  useEffect(() => {
    if (redactionData && Array.isArray(redactionData)) {
      const formatted = formatTagTreeData(redactionData);
      setRedactionTreeData(formatted);
    }
  }, [redactionData]);
  useEffect(() => {
    const list = reviewTagCountData?.data ?? [];
    if (!Array.isArray(list) || list.length === 0) return;

    const norm = (v?: string) => (v ?? '').toUpperCase();

    const tagIds = list
      .filter((item) => norm(item.tagType) === 'TAG')
      .map((item) => String(item.tagId));

    const classificationIds = list
      .filter((item) => norm(item.tagType) === 'CLASSIFICATION')
      .map((item) => String(item.tagId));

    const redactionIds = list
      .filter((item) => norm(item.tagType) === 'REDACTION')
      .map((item) => String(item.tagId));
    if (tagIds.length > 0) {
      setSelectedItem(tagIds);
    }
    if (classificationIds.length > 0) {
      setSelectedClassificationItems(classificationIds);
    }
    if (redactionIds.length > 0) {
      setSelectedRedactionItems(redactionIds);
    }
  }, [reviewTagCountData]);

  const handleSelected = (value: string[] | null) => {
    setSelectedItem(value);
  };
  const handleClassificationSelected = (value: string[] | null) => {
    setSelectedClassificationItems(value ?? []);
  };
  useEffect(() => {
    if (tagData && Array.isArray(tagData)) {
      const formatted = formatTagTreeData(tagData);
      if (formatted.length > 0) {
        setTreeData(
          wrapWithRootNode(formatted, {
            id: TAGS_ROOT_ID,
            label: TAGS_ROOT_LABEL,
            type: 'tags-root',
          })
        );
      } else {
        setTreeData([]);
      }
    } else {
      setTreeData([]);
    }
  }, [tagData, TAGS_ROOT_ID, TAGS_ROOT_LABEL]);
  useEffect(() => {
    if (redactionData && Array.isArray(redactionData)) {
      const formatted = formatTagTreeData(redactionData);
      if (formatted.length > 0) {
        setRedactionTreeData(
          wrapWithRootNode(formatted, {
            id: REDACTION_ROOT_ID,
            label: REDACTION_ROOT_LABEL,
            type: 'redaction-root',
          })
        );
      } else {
        setRedactionTreeData([]);
      }
    } else {
      setRedactionTreeData([]);
    }
  }, [redactionData, REDACTION_ROOT_ID, REDACTION_ROOT_LABEL]);

  const hasTags =
    treeData.length > 0 && (treeData[0].children?.length ?? 0) > 0;

  const hasClassifications =
    classificationTreeData.length > 0 &&
    (classificationTreeData[0].children?.length ?? 0) > 0;

  const hasRedactions =
    redactionTreeData.length > 0 &&
    (redactionTreeData[0].children?.length ?? 0) > 0;

  const queryClient = useQueryClient();
  const totalSelectedCount =
    (selectedItem?.length ?? 0) +
    (selectedClassificationItems?.length ?? 0) +
    (selectedRedactionItems?.length ?? 0);
  const {mutate: saveWidget} = useSaveTagClassificationDetails((data) => {
    queryClient.invalidateQueries({queryKey: ['tagClassification']});
    handleDrawer(false);
    onSuccess(data);
  });

  const handleSave = () => {
    const payload: ReviewTagCount = {
      tagIdList: (selectedItem ?? []).map((id) => Number(id)),
      classificationIdList: (selectedClassificationItems ?? []).map((id) =>
        Number(id)
      ),
      redactionTagIdList: (selectedRedactionItems ?? []).map((id) =>
        Number(id)
      ),
      widgetId: widgetIdMapping.tagging,
      isDefaultWidget: false,
      filter: null,
    };
    saveWidget(payload);
  };

  const renderTreeView = () => {
    return (
      <>
        {hasRedactions && (
          <div
            className="custom-tagging-tree-view"
            data-testid="ctw-redaction-tree"
          >
            <ArrTreeView
              itemsList={redactionTreeData}
              selectedItem={''}
              allowGlobalSelect={false}
              treeSelectedItems={selectedRedactionItems}
              multipleSelection={true}
              isShowCountLabel={true}
              isShowCheckbox={true}
              showActions={true}
              customContentHeight={36}
              setSelectedItem={(value) =>
                setSelectedRedactionItems(value ?? [])
              }
              defaultExpandedIds={
                selectedRedactionItems
                  ? [REDACTION_ROOT_ID, ...selectedRedactionItems]
                  : []
              }
            />
          </div>
        )}
        {hasClassifications && (
          <div
            className="custom-tagging-tree-view"
            data-testid="classification-tree"
          >
            <ArrTreeView
              itemsList={classificationTreeData}
              selectedItem={''}
              allowGlobalSelect={false}
              treeSelectedItems={selectedClassificationItems}
              multipleSelection={true}
              isShowCountLabel={true}
              isShowCheckbox={true}
              showActions={true}
              customContentHeight={36}
              setSelectedItem={handleClassificationSelected}
              defaultExpandedIds={
                selectedClassificationItems
                  ? [...selectedClassificationItems]
                  : []
              }
            />
          </div>
        )}
        {hasTags && (
          <div className="custom-tagging-tree-view" data-testid="ctw-tags-tree">
            <ArrTreeView
              itemsList={treeData}
              selectedItem={''}
              allowGlobalSelect={false}
              treeSelectedItems={selectedItem}
              multipleSelection={true}
              isShowCountLabel={true}
              isShowCheckbox={true}
              showActions={true}
              customContentHeight={36}
              setSelectedItem={handleSelected}
              defaultExpandedIds={
                selectedItem ? [...selectedItem, TAGS_ROOT_ID, '-1', '1'] : []
              }
            />
          </div>
        )}
      </>
    );
  };

  const contentBody = (
    <Box className="customize-tag-widget-container" data-testid="ctw-root">
      <Box data-testid="ctw-header">
        <WidgetsHeader
          mainTitle={t('dashboard.widgetCustomization.title')}
          subTitle={t('dashboard.widgetCustomization.tagging.title')}
          isSaveDisabled={
            totalSelectedCount === 0 || totalSelectedCount > MAX_COUNT
          }
          isCancelDisabled={false}
          onSave={handleSave}
          onCancel={() => handleDrawer(false)}
        />
        {/* Expose selection count for tests */}
        <span data-testid="ctw-selected-count" style={{display: 'none'}}>
          {totalSelectedCount}
        </span>
      </Box>

      <Box className="content-box" data-testid="ctw-content">
        <div className="custom-tagging-banner" data-testid="ctw-banner">
          <ArrBanner
            type="persistent"
            variant={totalSelectedCount > MAX_COUNT ? 'warning' : 'info'}
            bannerContent={t('dashboard.widgetCustomization.tagging.message')}
            bannerContentClass="body2_size"
            startIcon={totalSelectedCount > MAX_COUNT ? <InfoIcon /> : ''}
            dataTestId="customize-tag-banner"
          />
        </div>
        {isClassificationLoading || isRedationLoading || isTagLoading ? (
          <ArrLoadingSpinner className="tree-loading" />
        ) : (
          renderTreeView()
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {showDrawer && (
        <div data-testid="ctw-drawer">
          <ArrDrawer
            showButton={false}
            isOpen={showDrawer}
            size="small"
            side="right"
            onClose={() => handleDrawer(false)}
            showHeader={false}
          >
            {contentBody}
          </ArrDrawer>
        </div>
      )}
    </>
  );
};
